import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import LinkExternal02Icon from "@redotech/redo-icons/link-external-02.svg";
import ZapIcon from "@redotech/redo-icons/zap.svg";
import { ReceivingLabelField } from "@redotech/redo-model/inventory/receiving-label";
import {
  RedoManagedReturnsSortingMode,
  redoManagedReturnsSortingModeLabels,
} from "@redotech/redo-model/returns/sorting-types";
import { ReturnProcessingLevel } from "@redotech/redo-model/team";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Input } from "@redotech/ui/form";
import { memo, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { automationUrl, useMerchantAppLogin } from "../../../merchant-app";
import { AvailableReturnsCentersTable } from "./available-returns-centers-table";
import { SettingCard } from "./component/setting-card";
import type { RedoManagedReturnsSettingsForm } from "./redo-managed-returns-settings-form";

const processingLevelOptions: RedoListItem<ReturnProcessingLevel>[] =
  Object.values(ReturnProcessingLevel).map((returnProcessingLevel) => ({
    type: "text" as const,
    id: returnProcessingLevel,
    value: returnProcessingLevel,
    text:
      returnProcessingLevel.charAt(0).toUpperCase() +
      returnProcessingLevel.slice(1),
  }));

const redoManagedReturnsSortingModeOptions: RedoListItem<RedoManagedReturnsSortingMode>[] =
  Object.values(RedoManagedReturnsSortingMode).map((sortingMode) => ({
    type: "text" as const,
    id: sortingMode,
    value: sortingMode,
    text: redoManagedReturnsSortingModeLabels[sortingMode],
  }));

const receivingLabelFieldLabels: Record<ReceivingLabelField, string> = {
  [ReceivingLabelField.returnId]: "Return ID",
  [ReceivingLabelField.orderName]: "Order name",
};

const receivingLabelFieldOptions: RedoListItem<ReceivingLabelField>[] =
  Object.values(ReceivingLabelField).map((field) => ({
    type: "text" as const,
    id: field,
    value: field,
    text: receivingLabelFieldLabels[field],
  }));

export const RedoManagedReturnsSettingsCardV2 = memo(
  function RedoManagedReturnsSettingsCardV2({
    input,
    teamId,
    coveragePriceLimit,
    existingAutomationId,
  }: {
    input: RedoManagedReturnsSettingsForm;
    teamId: string;
    coveragePriceLimit: number;
    existingAutomationId: string | null;
  }) {
    const totalCoverageShare =
      (Number(input.inputs.processingCoverageShare.value) || 0) +
      (Number(input.inputs.consolidationCoverageShare.value) || 0);
    const exceedsCoveragePrice = totalCoverageShare > coveragePriceLimit;

    const openMerchantApp = useMerchantAppLogin();

    const handleOpenLocationFlow = useCallback(() => {
      openMerchantApp(teamId, "/settings/shipping/location-flow-editor");
    }, [openMerchantApp, teamId]);

    const selectedProcessingLevel = useMemo(() => {
      return processingLevelOptions.find(
        (option) => option.value === input.inputs.returnProcessingLevel.value,
      );
    }, [input.inputs.returnProcessingLevel.value]);

    const selectedSortingMode = useMemo(() => {
      return redoManagedReturnsSortingModeOptions.find(
        (option) => option.value === input.inputs.sortingMode.value,
      );
    }, [input.inputs.sortingMode.value]);

    const handleProcessingLevelSelected = useCallback(
      ({ item }: { item: RedoListItem<ReturnProcessingLevel> }) => {
        input.inputs.returnProcessingLevel.setValue(item.value);
      },
      [input.inputs.returnProcessingLevel],
    );

    const handleSortingModeSelected = useCallback(
      ({ item }: { item: RedoListItem<RedoManagedReturnsSortingMode> }) => {
        input.inputs.sortingMode.setValue(item.value);
      },
      [input.inputs.sortingMode],
    );

    const selectedReceivingLabelField = useMemo(() => {
      return receivingLabelFieldOptions.find(
        (option) =>
          option.value === input.inputs.receivingLabelEncodedField.value,
      );
    }, [input.inputs.receivingLabelEncodedField.value]);

    const handleReceivingLabelFieldSelected = useCallback(
      ({ item }: { item: RedoListItem<ReceivingLabelField> }) => {
        input.inputs.receivingLabelEncodedField.setValue(item.value);
      },
      [input.inputs.receivingLabelEncodedField],
    );

    const disabled = !input.inputs.enabled.value;

    const showReceivingLabel =
      input.inputs.returnProcessingLevel.value ===
      ReturnProcessingLevel.VERIFICATION;

    return (
      <>
        <BillingCard
          consolidationCoverageShare={input.inputs.consolidationCoverageShare}
          coveragePriceLimit={coveragePriceLimit}
          disabled={disabled}
          exceedsCoveragePrice={exceedsCoveragePrice}
          processingCost={input.inputs.processingCost}
          processingCoverageShare={input.inputs.processingCoverageShare}
          totalCoverageShare={totalCoverageShare}
        />
        <SettingCard>
          <Flex dir="column" gap="lg" p="xl">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              Operations
            </Text>
            <RedoSingleSelectDropdownInput
              disabled={disabled}
              label="Processing Level"
              options={processingLevelOptions}
              optionSelected={handleProcessingLevelSelected}
              placeholder="Select processing level..."
              selectedItem={selectedProcessingLevel}
              size="xs"
            />
            <RedoSingleSelectDropdownInput
              description="Controls this merchant's priority lane when the returns center uses priority sorting."
              disabled={disabled}
              label="Sorting mode"
              options={redoManagedReturnsSortingModeOptions}
              optionSelected={handleSortingModeSelected}
              placeholder="Select sorting mode..."
              selectedItem={selectedSortingMode}
              size="xs"
            />
            <RedoTextInput
              description="Minimum source inventory required before refulfillment inventory is exposed to sales channels."
              inputMode="numeric"
              inputTypeHint="number"
              label="Inventory push buffer"
              min={0}
              setValue={input.inputs.inventoryPushMinimumQuantity.setValue}
              state={disabled ? "disabled" : "default"}
              step={1}
              value={input.inputs.inventoryPushMinimumQuantity.value}
            />
            {showReceivingLabel && (
              <Flex dir="column" gap="lg">
                <RedoToggle
                  description="Print an additional QR receiving label, alongside the license plate, that this merchant's 3PL scans to receive the return."
                  disabled={disabled}
                  label="Receiving label"
                  setValue={input.inputs.receivingLabelEnabled.setValue}
                  size="sm"
                  value={input.inputs.receivingLabelEnabled.value}
                />
                {input.inputs.receivingLabelEnabled.value && (
                  <RedoSingleSelectDropdownInput
                    description="Data encoded in the receiving label's QR code and printed value."
                    disabled={disabled}
                    label="Encoded value"
                    options={receivingLabelFieldOptions}
                    optionSelected={handleReceivingLabelFieldSelected}
                    placeholder="Select encoded value..."
                    selectedItem={selectedReceivingLabelField}
                    size="xs"
                  />
                )}
              </Flex>
            )}
          </Flex>
        </SettingCard>
        <SettingCard>
          <Flex dir="column" gap="lg">
            <Flex align="center" justify="space-between" pt="xl" px="xl">
              <Text fontSize="sm" fontWeight="semibold" textColor="primary">
                Returns Centers
              </Text>
              <Flex align="center" gap="md">
                {existingAutomationId && (
                  <a
                    href={automationUrl(teamId, existingAutomationId)}
                    rel="noreferrer"
                    style={{
                      alignItems: "center",
                      display: "inline-flex",
                      gap: "var(--redo-spacing-xxs)",
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                    target="_blank"
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      textColor="brand-primary"
                    >
                      Refulfillment automation
                    </Text>
                    <ZapIcon
                      style={{
                        width: 14,
                        height: 14,
                        color: "var(--redo-colors-brand-500)",
                      }}
                    />
                  </a>
                )}
                <Flex
                  align="center"
                  gap="xxs"
                  onClick={handleOpenLocationFlow}
                  style={{ cursor: "pointer" }}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    textColor="brand-primary"
                  >
                    Configure location flow
                  </Text>
                  <LinkExternal02Icon
                    style={{
                      width: 14,
                      height: 14,
                      color: "var(--redo-colors-brand-500)",
                    }}
                  />
                </Flex>
              </Flex>
            </Flex>
            <AvailableReturnsCentersTable
              disabled={disabled}
              hubMappings={input.inputs.externalLocationHubMappings.value}
              input={input.inputs.availableReturnsCenters}
            />
          </Flex>
        </SettingCard>
      </>
    );
  },
);

const CHEVRON_BASE: React.CSSProperties = {
  width: 20,
  height: 20,
  transition: "transform 0.2s ease",
  color: "var(--redo-colors-text-text-tertiary-600)",
  flexShrink: 0,
};

const CHEVRON_OPEN: React.CSSProperties = {
  ...CHEVRON_BASE,
  transform: "rotate(90deg)",
};

const CHEVRON_CLOSED: React.CSSProperties = {
  ...CHEVRON_BASE,
  transform: "rotate(0deg)",
};

const CLICKABLE: React.CSSProperties = {
  cursor: "pointer",
  userSelect: "none",
};

const LINK_ICON_STYLE: React.CSSProperties = { width: 14, height: 14 };

const CoverageShareLabel = memo(function CoverageShareLabel() {
  return (
    <Flex align="center" gap="xs">
      <Text fontSize="xs" fontWeight="medium" textColor="secondary">
        Paid for by coverage
      </Text>
      <Link to="../returns/return-protection">
        <LinkExternal02Icon style={LINK_ICON_STYLE} />
      </Link>
    </Flex>
  );
});

const BillingCard = memo(function BillingCard({
  processingCost,
  processingCoverageShare,
  consolidationCoverageShare,
  disabled,
  exceedsCoveragePrice,
  totalCoverageShare,
  coveragePriceLimit,
}: {
  processingCost: Input<string>;
  processingCoverageShare: Input<string>;
  consolidationCoverageShare: Input<string>;
  disabled: boolean;
  exceedsCoveragePrice: boolean;
  totalCoverageShare: number;
  coveragePriceLimit: number;
}) {
  const [processingOpen, setProcessingOpen] = useState(true);
  const [consolidationOpen, setConsolidationOpen] = useState(true);

  return (
    <SettingCard>
      <div>
        <Flex dir="column" gap="lg" p="xl">
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Billing
          </Text>
          {exceedsCoveragePrice && (
            <Text fontSize="xs" textColor="error">
              {`Total coverage shares ($${totalCoverageShare.toFixed(2)}) exceed limit ($${coveragePriceLimit.toFixed(2)})`}
            </Text>
          )}
        </Flex>
      </div>
      <div>
        <Flex
          align="center"
          justify="space-between"
          onClick={() => setProcessingOpen((v) => !v)}
          p="xl"
          style={CLICKABLE}
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Processing
          </Text>
          <ChevronRightIcon
            style={processingOpen ? CHEVRON_OPEN : CHEVRON_CLOSED}
          />
        </Flex>
        {processingOpen && (
          <Flex align="flex-end" gap="md" pb="xl" pl="xl" pr="xl">
            <Flex grow="1">
              <RedoTextInput
                IconTrailing={() => <span>USD</span>}
                inputTypeHint="number"
                label="Per-return fee"
                min={0}
                placeholder="0.00"
                setValue={processingCost.setValue}
                state={disabled ? "disabled" : "default"}
                step={0.01}
                value={processingCost.value}
              />
            </Flex>
            <Flex pb="sm">
              <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
                or
              </Text>
            </Flex>
            <Flex dir="column" gap="xs" grow="1">
              <CoverageShareLabel />
              <RedoTextInput
                IconTrailing={() => <span>USD</span>}
                inputTypeHint="number"
                min={0}
                placeholder="0.00"
                setValue={processingCoverageShare.setValue}
                state={
                  disabled
                    ? "disabled"
                    : exceedsCoveragePrice
                      ? "error"
                      : "default"
                }
                step={0.01}
                value={processingCoverageShare.value}
              />
            </Flex>
          </Flex>
        )}
      </div>
      <div>
        <Flex
          align="center"
          justify="space-between"
          onClick={() => setConsolidationOpen((v) => !v)}
          p="xl"
          style={CLICKABLE}
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Consolidation
          </Text>
          <ChevronRightIcon
            style={consolidationOpen ? CHEVRON_OPEN : CHEVRON_CLOSED}
          />
        </Flex>
        {consolidationOpen && (
          <Flex dir="column" gap="sm" pb="xl" pl="xl" pr="xl">
            <CoverageShareLabel />
            <RedoTextInput
              IconTrailing={() => <span>USD</span>}
              inputTypeHint="number"
              min={0}
              placeholder="0.00"
              setValue={consolidationCoverageShare.setValue}
              state={
                disabled
                  ? "disabled"
                  : exceedsCoveragePrice
                    ? "error"
                    : "default"
              }
              step={0.01}
              value={consolidationCoverageShare.value}
            />
            <Text fontSize="xs" textColor="tertiary">
              If empty, shipping costs are passed through to the merchant.
            </Text>
          </Flex>
        )}
      </div>
    </SettingCard>
  );
});
